import {
    FloatingPortal,
    FloatingFocusManager,
    useFloating,
    useDismiss,
    useRole,
    useInteractions,
} from '@floating-ui/react';
import useFloatingProps from 'Hooks/useFloatingProps';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from 'Utils/cn';

const sizeClasses = {
    xs: 'w-64',
    sm: 'w-80',
    md: 'w-[496px]',
    lg: 'w-[600px]',
};

export default function Drawer(props) {
    const {
        open,
        className,
        children,
        onClose,
        size = 'md',
        anchor = 'right',
        zIndex,
        classes = {},
    } = useFloatingProps(props);

    const { refs, context } = useFloating({
        open,
        onOpenChange: (open) => {
            if (!open) onClose?.();
        },
        placement: anchor === 'left' ? 'left-start' : 'right-start',
    });

    const { getFloatingProps } = useInteractions([
        useRole(context, { role: 'dialog' }),
        useDismiss(context, {
            outsidePressEvent: 'mousedown',
            escapeKey: true,
            // Don't dismiss the drawer when clicking inside a dialog overlay
            outsidePress: (event) => !event.target.closest('.z-modal-backdrop'),
        }),
    ]);

    return (
        <AnimatePresence>
            {open && (
                <FloatingPortal>
                    <div
                        className={cn(
                            'fixed inset-0',
                            zIndex == null && 'z-[var(--z-index-offcanvas)]',
                            classes.wrapper,
                        )}
                        style={zIndex == null ? undefined : { zIndex }}
                    >
                        <FloatingFocusManager context={context} modal={false}>
                            <motion.div
                                ref={refs.setFloating}
                                {...getFloatingProps({
                                    className: cn(
                                        'absolute top-0 h-full bg-white shadow-lg max-w-[calc(100vw-56px)] overflow-y-auto transition-all',
                                        anchor === 'right' ? 'right-0' : 'left-0',
                                        sizeClasses[size],
                                        className,
                                    ),
                                })}
                                style={{}}
                                tabIndex={-1}
                                aria-modal="true"
                                initial={{
                                    x: anchor === 'right' ? '100%' : '-100%',
                                    opacity: 1,
                                }}
                                animate={{
                                    x: 0,
                                    opacity: 1,
                                }}
                                exit={{
                                    x: anchor === 'right' ? '100%' : '-100%',
                                    opacity: 0.7,
                                }}
                                transition={{ ease: 'easeInOut', duration: 0.2 }}
                            >
                                {children}
                            </motion.div>
                        </FloatingFocusManager>
                    </div>
                </FloatingPortal>
            )}
        </AnimatePresence>
    );
}
